import { ShieldCheck, ShieldAlert, ShieldX, type LucideIcon } from 'lucide-react';

// ─── Single source of truth for AusIndustry / Division 355 defensibility ───────────────────
// Shared by the project defensibility widget, the claim readiness evidence log, and the
// dashboard defensibility matrix. Keep this the ONLY place criterion order, labels, and score
// presentation are defined.
//
// Cache-key sharing note: the widget and the evidence log use `defensibilityKeys` for their
// per-project checks + evidence queries, so they share one cache entry and a refresh in either
// invalidates the other. The dashboard matrix does NOT use these keys — it fetches all projects
// under its own aggregate key (`['defensibility-matrix', …]`) and only borrows the types +
// score labels here.

export type DefensibilityCriterion =
  | 'systematic_progression'
  | 'technical_uncertainty'
  | 'new_knowledge'
  | 'contemporaneous_documentation';

export type DefensibilityScore = 'strong' | 'moderate' | 'weak' | 'gap';

// Canonical criterion order (matches the four Division 355 eligibility gates).
export const ORDERED_CRITERIA: DefensibilityCriterion[] = [
  'systematic_progression',
  'technical_uncertainty',
  'new_knowledge',
  'contemporaneous_documentation',
];

export const CRITERION_LABELS: Record<DefensibilityCriterion, string> = {
  systematic_progression: 'Systematic Progression',
  technical_uncertainty: 'Technical Uncertainty',
  new_knowledge: 'New Knowledge',
  contemporaneous_documentation: 'Documentation Coverage',
};

export type DefensibilityBadgeVariant = 'positive' | 'warning' | 'destructive';

export interface ScoreMeta {
  label: string;
  // Icon COMPONENT (not a pre-built element) so each consumer can size/colour it itself —
  // status is always icon + text, never colour-only (§6.2).
  Icon: LucideIcon;
  badgeVariant: DefensibilityBadgeVariant;
}

export const SCORE_META: Record<DefensibilityScore, ScoreMeta> = {
  strong: { label: 'Strong', Icon: ShieldCheck, badgeVariant: 'positive' },
  moderate: { label: 'Moderate', Icon: ShieldAlert, badgeVariant: 'warning' },
  weak: { label: 'Weak', Icon: ShieldAlert, badgeVariant: 'warning' },
  gap: { label: 'Gap', Icon: ShieldX, badgeVariant: 'destructive' },
};

// React Query cache keys. The widget and the evidence log MUST use these identical keys so
// they share one cache entry — a "Refresh All" in the widget then invalidates the evidence
// log's data too (and vice versa). Do not inline these keys anywhere.
export const defensibilityKeys = {
  checks: (projectId: string) => ['defensibility', projectId] as const,
  evidence: (projectId: string, criterion: DefensibilityCriterion) =>
    ['defensibility-evidence', projectId, criterion] as const,
};
